import { createApp } from 'vue';
import http from './plugins/http';
import { LazyLoader } from './utils/LazyLoader.js';
import SearchBar from '../components/search/SearchBar.vue';

// Browse Categories slider
function initBrowseCats() {
	const track = document.getElementById('browseCatsTrack');
	const left  = document.getElementById('browseLeft');
	const right = document.getElementById('browseRight');
	if (!track || !left || !right) return;

	const step = 284; // card 270 + gap 14
	left.addEventListener('click',  () => track.scrollBy({ left: -step * 3, behavior: 'smooth' }));
	right.addEventListener('click', () => track.scrollBy({ left:  step * 3, behavior: 'smooth' }));
}

// Featured Slider
function initSlider() {
	const slides = document.querySelectorAll('.slide');
	const dots   = document.querySelectorAll('.slider-dot');
	const prev   = document.getElementById('sliderPrev');
	const next   = document.getElementById('sliderNext');
	const glowL  = document.getElementById('glowLeft');
	const glowR  = document.getElementById('glowRight');
	const glowT  = document.getElementById('glowTop');

	if( ! slides.length )
		return;

	let current = 0;
	let autoplay;

	const total = slides.length;

	function goTo(n) {
		current = n;
		slides.forEach((s, i) => {
			s.style.opacity      = i === n ? '1' : '0';
			s.style.zIndex       = i === n ? '2' : '1';
			s.style.pointerEvents = i === n ? 'auto' : 'none';
		});
		dots.forEach((d, i) => d.classList.toggle('active', i === n));
		// Glow colors
		const s = slides[n];
		if (glowL) glowL.style.background = s.dataset.glowL || 'transparent';
		if (glowR) glowR.style.background = s.dataset.glowR || 'transparent';
		if (glowT) glowT.style.background = s.dataset.glowT || 'transparent';
	}

	function nextSlide() {
		goTo((current + 1) % total);
	}

	function prevSlide() {
		goTo((current - 1 + total) % total);
	}

	function resetAuto() {
		clearInterval( autoplay );
		autoplay = setInterval( nextSlide, 5000 );
	}

	if( prev )
		prev.addEventListener('click', () => {
			prevSlide();
			resetAuto();
		});

	if( next )
		next.addEventListener('click', () => {
			nextSlide();
			resetAuto();
		});

	dots.forEach(d => d.addEventListener('click', () => {
		goTo(+d.dataset.slide);
		resetAuto();
	}));

	// Touch swipe
	let touchX = 0;
	const vp = document.querySelector('.slider-viewport');
	if( vp ) {
		vp.addEventListener('touchstart', e => {
			touchX = e.touches[0].clientX;
		}, { passive: true });
		vp.addEventListener('touchend', e => {
			const diff = touchX - e.changedTouches[0].clientX;
			if (Math.abs(diff) > 50) {
				diff > 0 ? nextSlide() : prevSlide();
				resetAuto();
			}
		});
	}

	// Pause on hover
	const wrap = document.querySelector('.slider-wrap');
	if (wrap) {
		wrap.addEventListener('mouseenter', () => clearInterval(autoplay));
		wrap.addEventListener('mouseleave', () => resetAuto());
	}

	goTo(0);
	resetAuto();
}

// Featured Story carousel (лёгкий модуль: fade + dots + arrows + swipe)
function initStory() {
	const root = document.getElementById('vStoryCarousel');
	if( ! root )
		return;

	const slides = root.querySelectorAll('.story-slide');
	if( slides.length < 2 )
		return;

	const dots = root.querySelectorAll('.story-dot');
	const prev = document.getElementById('storyPrev');
	const next = document.getElementById('storyNext');

	let current = 0;
	let autoplay;
	const total = slides.length;

	function goTo(n) {
		current = n;
		slides.forEach((s, i) => s.classList.toggle('active', i === n));
		dots.forEach((d, i) => d.classList.toggle('active', i === n));
	}

	function nextSlide() { goTo((current + 1) % total); }
	function prevSlide() { goTo((current - 1 + total) % total); }

	function resetAuto() {
		clearInterval( autoplay );
		autoplay = setInterval( nextSlide, 6000 );
	}

	if( prev )
		prev.addEventListener('click', () => { prevSlide(); resetAuto(); });

	if( next )
		next.addEventListener('click', () => { nextSlide(); resetAuto(); });

	dots.forEach(d => d.addEventListener('click', () => {
		goTo(+d.dataset.story);
		resetAuto();
	}));

	// Touch swipe
	let touchX = 0;
	root.addEventListener('touchstart', e => {
		touchX = e.touches[0].clientX;
	}, { passive: true });
	root.addEventListener('touchend', e => {
		const diff = touchX - e.changedTouches[0].clientX;
		if( Math.abs(diff) > 50 ) {
			diff > 0 ? nextSlide() : prevSlide();
			resetAuto();
		}
	});

	// Pause on hover
	root.addEventListener('mouseenter', () => clearInterval(autoplay));
	root.addEventListener('mouseleave', () => resetAuto());

	goTo(0);
	resetAuto();
}

// Collections bar scroll
function initCollections() {
	const track = document.getElementById('collectionsTrack');
	const prev  = document.getElementById('collPrev');
	const next  = document.getElementById('collNext');
	if (!track) return;

	const scrollStep = 200;

	function updateArrows() {
		if (!prev || !next) return;
		prev.classList.toggle('coll-arrow--hidden', track.scrollLeft <= 0);
		next.classList.toggle('coll-arrow--hidden', track.scrollLeft >= track.scrollWidth - track.clientWidth - 1);
	}

	// Стрелки
	if (prev) prev.addEventListener('click', () => {
		track.scrollBy({ left: -scrollStep, behavior: 'smooth' });
	});

	if (next) next.addEventListener('click', () => {
		track.scrollBy({ left: scrollStep, behavior: 'smooth' });
	});

	// Колёсико → горизонтальный скролл
	track.addEventListener('wheel', e => {
		if (Math.abs(e.deltaY) < 5) return;
		e.preventDefault();
		track.scrollBy({ left: e.deltaY, behavior: 'smooth' });
	}, { passive: false });

	track.addEventListener('scroll', updateArrows, { passive: true });
	updateArrows();
}

// Faq accordion
function initFaqAccordion() {
	const lists = document.querySelectorAll('.pp-faq-list');
	if( !lists.length ) return;

	lists.forEach( list => {
		list.addEventListener( 'click', e => {
			const q = e.target.closest( '.pp-faq-q' );
			if( !q ) return;
			const item    = q.closest( '.pp-faq-item' );
			const isOpen  = item.classList.contains( 'open' );
			list.querySelectorAll( '.pp-faq-item.open' ).forEach( el => el.classList.remove( 'open' ) );
			if( !isOpen ) item.classList.add( 'open' );
		});
	});
}

// Mega Menu (Categories)
function initMegaMenu() {
	const catBtn   = document.getElementById( 'catBtn' );
	const megaMenu = document.getElementById( 'megaMenu' );
	const overlay  = document.getElementById( 'megaOverlay' );
	const toggle   = document.getElementById( 'mobileToggle' );
	const mobileButtonSearch = document.getElementById( 'mobileButtonSearch' );
	const searchPanel = document.getElementById( 'vSearch' );
	const panel    = document.getElementById( 'mobilePanel' );
	const companyBtn  = document.getElementById( 'navCompanyBtn' );
	const companyWrap = document.getElementById( 'navCompany' );

	const accountBtn  = document.getElementById('navAccountBtn');
	const accountWrap = document.getElementById('navAccount');

	// Account dropdown (открытие закрывает company)
	if( accountBtn && accountWrap ) {

		accountBtn.addEventListener('click', ( e ) => {
			e.stopPropagation();
			if( companyWrap ) companyWrap.classList.remove('open');
			accountWrap.classList.toggle('open');
		});
	}

	// Mega menu
	if( catBtn && megaMenu ) {

		function openMega() {
			catBtn.classList.add( 'open' );
			megaMenu.classList.add( 'open' );
			if( overlay ) overlay.classList.add( 'open' );
			document.body.style.overflow = 'hidden';
		}

		function closeMega() {
			catBtn.classList.remove( 'open' );
			megaMenu.classList.remove( 'open' );
			if( overlay ) overlay.classList.remove( 'open' );
			document.body.style.overflow = '';
		}

		catBtn.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			catBtn.classList.contains( 'open' ) ? closeMega() : openMega();
		});

		document.addEventListener( 'keydown', ( e ) => {
			if( e.key === 'Escape' ) {
				closeMega();

				closeMobile();

				if( companyWrap )
					companyWrap.classList.remove( 'open' );
			}
		});
	}

	// Mobile menu
	if( toggle && panel ) {

		function openMobile() {
			toggle.classList.add( 'open' );
			panel.classList.add( 'open' );
			document.body.style.overflow = 'hidden';
			// Убираем панель поиска при открытии меню
			if (searchPanel && searchPanel.classList.contains('open')) {
				closeSearchPanel();
			}
		}

		function closeMobile() {
			toggle.classList.remove( 'open' );
			panel.classList.remove( 'open' );
			document.body.style.overflow = '';
		}

		toggle.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			panel.classList.contains( 'open' ) ? closeMobile() : openMobile();
		});
	}

	// Search mobile
	function openSearchPanel() {
		mobileButtonSearch.classList.add( 'open' );
		searchPanel.classList.add( 'open' );
	}

	function closeSearchPanel() {
		mobileButtonSearch.classList.remove( 'open' );
		searchPanel.classList.remove( 'open' );
	}

	if (mobileButtonSearch && searchPanel) {
		mobileButtonSearch.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			searchPanel.classList.contains( 'open' ) ? closeSearchPanel() : openSearchPanel();
		});
	}

	// Company dropdown (открытие закрывает account)
	if( companyBtn && companyWrap ) {

		companyBtn.addEventListener( 'click', ( e ) => {
			e.stopPropagation();
			if( accountWrap ) accountWrap.classList.remove('open');
			companyWrap.classList.toggle( 'open' );
		});
	}

	// Один общий обработчик клика вне
	document.addEventListener( 'click', ( e ) => {
		if( catBtn && megaMenu && ! catBtn.contains( e.target ) && ! megaMenu.contains( e.target ) ) {

			catBtn.classList.remove( 'open' );
			megaMenu.classList.remove( 'open' );

			if( overlay ) overlay.classList.remove( 'open' );
				document.body.style.overflow = '';
		}

		if( toggle && panel && ! toggle.contains( e.target ) && ! panel.contains( e.target ) ) {
			toggle.classList.remove( 'open' );
			panel.classList.remove( 'open' );
			document.body.style.overflow = '';
		}

		if( companyWrap && ! companyWrap.contains( e.target ) )
		companyWrap.classList.remove( 'open' );

		if( accountWrap && ! accountWrap.contains( e.target ) )
			accountWrap.classList.remove( 'open' );
	});
}

// Init
document.addEventListener('DOMContentLoaded', () => {

	// Vue search
	const searchEl = document.getElementById( 'vSearch' );
	if( searchEl ) {
		const sc = createApp( SearchBar );
		sc.use( http );
		sc.mount( searchEl );
	}

	// Lazy loader
	window.lazyLoader = new LazyLoader();
	window.lazyLoader.init();

	initMegaMenu();
	initSlider();
	initStory();
	initBrowseCats();
	initCollections();
	initFaqAccordion();
});